/**
 * PATH: packages/job-hub/src/jobs/qa/qaProductSearch.ts
 * FILE_ID: JOBHUB-QA-PRODUCT-SEARCH-001
 * PURPOSE: Pricelist name lookup for the QA product field.
 * WHY: Same endpoint quote-builder's picker uses, so QA and quoting search one
 *      catalogue. QA wants the NAME only — no price, supplier, category or
 *      unit — so the extra wire fields are dropped here rather than in the UI.
 *      job-hub cannot import from @quotech/quote-builder (module boundary
 *      lint), and the URL contract is two lines, so it is restated, not forked.
 * LANDMINE: always resolves. A failed lookup must leave the operator typing
 *      free text, never block the field or throw into the row.
 */
import type { JobHubHttpClient } from '../jobHubDataAdapter'

export interface QaProductOption {
  id: string
  name: string
}

/** Server hard-caps at 50; a phone list wants far fewer. */
const DEFAULT_LIMIT = 8

export function buildQaProductSearchUrl(q: string, limit = DEFAULT_LIMIT): string {
  const params = new URLSearchParams()
  const term = q.trim()
  if (term) params.set('q', term)
  params.set('limit', String(limit))
  return `/api/quotebuilder/products/search/?${params.toString()}`
}

export async function searchQaProducts(
  client: JobHubHttpClient,
  q: string,
  limit = DEFAULT_LIMIT,
): Promise<QaProductOption[]> {
  if (!q.trim()) return []
  try {
    const { data } = await client.get<Array<{ id?: string; item_name?: string }>>(
      buildQaProductSearchUrl(q, limit),
    )
    if (!Array.isArray(data)) return []
    return data
      .map((row) => ({ id: String(row?.id ?? ''), name: (row?.item_name ?? '').trim() }))
      .filter((row) => row.name.length > 0)
  } catch {
    return []
  }
}
